import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { workorders, buildings, contractors, voiceWorkorder } from '../services/api';

const STATUS_OPTS = ['requested', 'quoted', 'approved', 'in_progress', 'completed', 'cancelled'];
const PRIORITY_OPTS = ['low', 'medium', 'high', 'urgent'];

const STATUS_COLORS = {
  requested:   'bg-yellow-100 text-yellow-800',
  quoted:      'bg-blue-100 text-blue-800',
  approved:    'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed:   'bg-green-100 text-green-800',
  cancelled:   'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

function WorkOrderModal({ order, buildingList, contractorList, onClose, onSave }) {
  const [form, setForm] = useState(order || {
    title: '', description: '', status: 'requested', priority: 'medium',
    building_id: buildingList[0]?.id || '', contractor_id: '',
    unit_number: '', quoted_amount: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (order?.id) {
        await workorders.update(order.id, form);
        toast.success('Work order updated');
      } else {
        await workorders.create(form);
        toast.success('Work order created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">{order?.id ? 'Edit' : 'New'} Work Order</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">Title *</label>
            <input className="input" value={form.title} onChange={set('title')} required
              placeholder="e.g. Roof leak repair – Unit 304" />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input resize-none" rows={3} value={form.description || ''}
              onChange={set('description')} placeholder="Describe the issue…" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Building *</label>
              <select className="input" value={form.building_id} onChange={set('building_id')} required>
                <option value="">Select building…</option>
                {buildingList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Unit #</label>
              <input className="input" value={form.unit_number || ''} onChange={set('unit_number')} placeholder="304" />
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input" value={form.status} onChange={set('status')}>
                {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Priority</label>
              <select className="input" value={form.priority} onChange={set('priority')}>
                {PRIORITY_OPTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Contractor</label>
              <select className="input" value={form.contractor_id || ''} onChange={set('contractor_id')}>
                <option value="">Not assigned</option>
                {contractorList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Quoted Amount ($)</label>
              <input type="number" className="input" value={form.quoted_amount || ''}
                onChange={set('quoted_amount')} placeholder="0.00" min="0" step="0.01" />
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={2} value={form.notes || ''} onChange={set('notes')} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WorkOrders() {
  const [orders, setOrders] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [contractorList, setContractorList] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [modal, setModal] = useState(null);
  const [voiceModal, setVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [parsingVoice, setParsingVoice] = useState(false);
  const recognitionRef = React.useRef(null);
  const [autopilotModal, setAutopilotModal] = useState(false);
  const [autopilotText, setAutopilotText] = useState('');
  const [autopilotParsing, setAutopilotParsing] = useState(false);
  const [autopilotPreview, setAutopilotPreview] = useState(null);
  const [autopilotConfirming, setAutopilotConfirming] = useState(false);

  const load = () => {
    workorders.list({ status: filterStatus, priority: filterPriority })
      .then((r) => setOrders(r.data))
      .catch(() => toast.error('Failed to load work orders'));
  };

  useEffect(() => {
    load();
    buildings.list().then((r) => setBuildingList(r.data)).catch(() => {});
    contractors.list().then((r) => setContractorList(r.data)).catch(() => {});
  }, [filterStatus, filterPriority]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error('Voice requires Chrome or Edge');
    const r = new SR(); r.continuous = true; r.interimResults = true; r.lang = 'en-CA';
    let final = '';
    r.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + ' ';
        else interim += e.results[i][0].transcript;
      }
      setVoiceTranscript(final + interim);
    };
    r.onend = () => { setVoiceTranscript(final); setIsRecording(false); };
    recognitionRef.current = r; r.start(); setIsRecording(true);
  };

  const stopVoice = () => { recognitionRef.current?.stop(); setIsRecording(false); };

  const handleParseVoice = async () => {
    if (!voiceTranscript.trim()) return toast.error('Record something first');
    setParsingVoice(true);
    try {
      const res = await voiceWorkorder.parse(voiceTranscript);
      setVoiceModal(false); setVoiceTranscript('');
      setModal({ ...res.data, _prefilled: true });
      toast.success('AI filled in the work order — review and save');
    } catch (err) { toast.error('Failed to parse voice'); }
    finally { setParsingVoice(false); }
  };

  const handleAutopilotParse = async () => {
    if (!autopilotText.trim()) return toast.error('Describe the issue first');
    setAutopilotParsing(true);
    setAutopilotPreview(null);
    try {
      const res = await workorders.autopilot({ description: autopilotText });
      setAutopilotPreview(res.data.preview);
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI parsing failed');
    } finally {
      setAutopilotParsing(false);
    }
  };

  const handleAutopilotConfirm = async () => {
    if (!autopilotPreview?.building_id) {
      return toast.error('Building not detected — use "Edit First" to set it manually');
    }
    setAutopilotConfirming(true);
    try {
      await workorders.autopilot({ description: autopilotText, confirm: true });
      toast.success('🤖 Work order created by AI!');
      setAutopilotModal(false);
      setAutopilotText('');
      setAutopilotPreview(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create work order');
    } finally {
      setAutopilotConfirming(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this work order?')) return;
    await workorders.delete(id);
    toast.success('Work order deleted');
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
          <p className="text-gray-500 text-sm mt-1">{orders.length} total</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setAutopilotModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm font-medium rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
            Autopilot
          </button>
          <button onClick={() => setVoiceModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
            Voice
          </button>
          <button onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Work Order
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select className="input w-44" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select className="input w-40" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
          <option value="">All priorities</option>
          {PRIORITY_OPTS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Cards */}
      {orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center text-gray-400">
          <p className="text-lg">No work orders found.</p>
          <button onClick={() => setModal('new')} className="mt-2 text-sm text-blue-600 hover:underline">
            Create your first work order →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-xl border border-gray-200 px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[o.priority]}`}>
                      {o.priority}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status]}`}>
                      {o.status.replace('_', ' ')}
                    </span>
                    {o.unit_number && (
                      <span className="text-xs text-gray-400">Unit {o.unit_number}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900">{o.title}</h3>
                  <div className="flex gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                    <span>{o.building_name}</span>
                    {o.contractor_name && <span>· {o.contractor_name}</span>}
                    {o.quoted_amount && <span>· ${o.quoted_amount.toLocaleString()}</span>}
                    <span>· {new Date(o.created_at).toLocaleDateString()}</span>
                  </div>
                  {o.description && (
                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">{o.description}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setModal(o)} className="text-xs text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => handleDelete(o.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Autopilot modal */}
      {autopilotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="text-base">🤖</span> AI Work Order Autopilot
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Describe the issue — AI creates + assigns the work order</p>
              </div>
              <button onClick={() => { setAutopilotModal(false); setAutopilotText(''); setAutopilotPreview(null); }}
                className="p-1 hover:bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="label">Describe the issue *</label>
                <textarea
                  className="input resize-none"
                  rows={4}
                  value={autopilotText}
                  onChange={e => setAutopilotText(e.target.value)}
                  placeholder='e.g. "Urgent pipe leak in unit 205, water coming through the ceiling" or "The hallway lights on floor 3 of Sunset Tower have been flickering all week"'
                />
              </div>

              <button
                onClick={handleAutopilotParse}
                disabled={autopilotParsing || !autopilotText.trim()}
                className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
              >
                {autopilotParsing ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
                    AI is analyzing…
                  </>
                ) : '✨ Parse with AI'}
              </button>

              {/* Preview Card */}
              {autopilotPreview && (
                <div className="border border-violet-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-violet-50 border-b border-violet-100">
                    <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">AI Preview — Review before confirming</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-400">Title</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{autopilotPreview.title}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-gray-400">Priority</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 inline-block ${PRIORITY_COLORS[autopilotPreview.priority] || ''}`}>{autopilotPreview.priority}</span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Category</p>
                        <p className="text-xs font-medium text-gray-700 mt-0.5 capitalize">{autopilotPreview.category}</p>
                      </div>
                      {autopilotPreview.unit_number && (
                        <div>
                          <p className="text-xs text-gray-400">Unit</p>
                          <p className="text-xs font-medium text-gray-700 mt-0.5">{autopilotPreview.unit_number}</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Building</p>
                      <p className={`text-sm mt-0.5 ${autopilotPreview.building_name ? 'text-gray-800 font-medium' : 'text-orange-600'}`}>
                        {autopilotPreview.building_name || '⚠️ Not detected — use Edit First'}
                      </p>
                    </div>
                    {autopilotPreview.description && (
                      <div>
                        <p className="text-xs text-gray-400">Description</p>
                        <p className="text-sm text-gray-600 mt-0.5">{autopilotPreview.description}</p>
                      </div>
                    )}
                    {autopilotPreview.suggested_contractor ? (
                      <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-green-600 font-medium">✅ Auto-assigned contractor</p>
                        <p className="text-sm font-semibold text-gray-900 mt-0.5">{autopilotPreview.suggested_contractor.name}</p>
                        <p className="text-xs text-gray-500">{autopilotPreview.suggested_contractor.specialty} · ⭐ {autopilotPreview.suggested_contractor.rating || 'N/A'}</p>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2">
                        <p className="text-xs text-yellow-700">⚠️ No contractor matched — will be unassigned</p>
                      </div>
                    )}
                  </div>
                  <div className="px-4 pt-3 pb-1">
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                      <svg className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Review before confirming.</strong> AI suggestions may need adjustment — you have final approval.</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex gap-3">
                    <button
                      onClick={() => {
                        setAutopilotModal(false);
                        setAutopilotText('');
                        setModal({
                          title: autopilotPreview.title,
                          description: autopilotPreview.description,
                          priority: autopilotPreview.priority,
                          building_id: autopilotPreview.building_id || (buildingList[0]?.id || ''),
                          unit_number: autopilotPreview.unit_number || '',
                          contractor_id: autopilotPreview.suggested_contractor?.id || '',
                          notes: autopilotPreview.notes || '',
                          status: 'requested',
                        });
                        setAutopilotPreview(null);
                      }}
                      className="flex-1 py-2 border border-gray-300 hover:bg-gray-50 text-sm font-medium rounded-lg text-gray-700 transition-colors"
                    >
                      ✏️ Edit First
                    </button>
                    <button
                      onClick={handleAutopilotConfirm}
                      disabled={autopilotConfirming || !autopilotPreview.building_id}
                      className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                    >
                      {autopilotConfirming ? 'Creating…' : '✅ Confirm & Create'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Voice work order modal */}
      {voiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Voice Work Order</h2>
            <p className="text-sm text-gray-500 mb-4">Speak the issue — AI will create the work order for you.</p>
            <div className="flex justify-center mb-4">
              <button onClick={isRecording ? stopVoice : startVoice}
                className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-purple-600 hover:bg-purple-700'}`}>
                {isRecording
                  ? <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
                  : <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/></svg>
                }
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mb-3">{isRecording ? '🔴 Recording…' : 'Click to start'}</p>
            <textarea className="w-full border border-gray-200 rounded-lg p-3 text-sm min-h-20 resize-none outline-none"
              placeholder='e.g. "Urgent pipe leak in unit 301, assign to Sunny the plumber"'
              value={voiceTranscript} onChange={e => setVoiceTranscript(e.target.value)} />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setVoiceModal(false); setVoiceTranscript(''); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleParseVoice} disabled={parsingVoice || !voiceTranscript.trim()}
                className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50">
                {parsingVoice ? 'AI parsing…' : 'Create with AI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <WorkOrderModal
          order={modal === 'new' ? null : modal}
          buildingList={buildingList}
          contractorList={contractorList}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}

      <style>{`
        .label { display: block; font-size: 0.75rem; font-weight: 500; color: #374151; margin-bottom: 0.25rem; }
        .input { width: 100%; border: 1px solid #d1d5db; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
      `}</style>
    </div>
  );
}
